import { ScreenElement, Engine, vec, Canvas } from "excalibur";
import { CrtShader } from '../shaders/crt';

// Import WASM module
import { init_terminal, handle_key_up } from '../../pkg/rust_terminal';
import { wasmReady } from '../wasm-loader';

// Ratzilla grid size - higher resolution for better game graphics
const ratzillaWidth = 800;
const ratzillaHeight = 500;

// Calculate terminal display size (90% of viewport)
function getTerminalSize() {
    const width = Math.floor(window.innerWidth * 0.9);
    const height = Math.floor(width * 0.6);
    return { width, height };
}

export class Terminal extends ScreenElement {
    private terminalElement: HTMLDivElement;
    private engine: Engine;
    private wasmInitialized: boolean = false;
    private terminalWidth: number;
    private terminalHeight: number;

    constructor() {
        const size = getTerminalSize();
        super({
            name: 'Terminal',
            pos: vec(0, 0),
            width: size.width,
            height: size.height,
        });
        this.terminalWidth = size.width;
        this.terminalHeight = size.height;

        // Create container for Ratzilla canvas
        this.terminalElement = document.createElement('div');
        this.terminalElement.id = 'ratzilla-terminal';
        this.terminalElement.style.position = 'absolute';
        this.terminalElement.style.top = '0';
        this.terminalElement.style.left = '0';
        this.terminalElement.style.width = `${ratzillaWidth}px`;
        this.terminalElement.style.height = `${ratzillaHeight}px`;
        this.terminalElement.style.visibility = 'hidden';
        this.terminalElement.style.zIndex = '-10000';
        document.body.appendChild(this.terminalElement);
    }

    override async onInitialize(engine: Engine) {
        this.engine = engine;
        this.transform.pos = vec(engine.screen.width / 2 - this.terminalWidth / 2, engine.screen.height / 2 - this.terminalHeight / 2);

        // Apply CRT shader
        const material = new CrtShader().create();
        this.graphics.material = material;

        // Initialize WASM module
        await this.initWasm();

        // Wait for canvas to be created by Ratzilla
        await this.waitForCanvas();

        // Get the Ratzilla canvas
        const terminalCanvas = this.terminalElement.querySelector('canvas');
        if (!terminalCanvas) {
            console.error('Ratzilla canvas not found');
            return;
        }

        // Create Excalibur Canvas wrapper
        const canvas = new Canvas({
            width: this.terminalWidth,
            height: this.terminalHeight,
            cache: false,
            draw: (ctx) => {
                if (terminalCanvas) {
                    ctx.drawImage(
                        terminalCanvas,
                        0, 0, terminalCanvas.width, terminalCanvas.height,
                        0, 0, ctx.canvas.width, ctx.canvas.height
                    );
                }
            }
        });

        this.graphics.add(canvas);
        this.z = 100;

        // Focus handling
        this.on('pointerdown', () => {
            // Ratzilla handles focus internally via keyboard events on document
            this.terminalElement.focus();
        });
    }

    private async initWasm() {
        if (this.wasmInitialized) return;

        try {
            // Wait for preloaded WASM module
            await wasmReady;

            // Initialize the terminal with container ID and size
            init_terminal('ratzilla-terminal', ratzillaWidth, ratzillaHeight);

            // Add keyup listener for game controls
            window.addEventListener('keyup', (e) => {
                handle_key_up(e.key);
            });

            this.wasmInitialized = true;
            console.log('WASM terminal initialized');
        } catch (error) {
            console.error('Failed to initialize WASM:', error);
        }
    }

    private waitForCanvas(): Promise<void> {
        return new Promise((resolve) => {
            const check = () => {
                const canvas = this.terminalElement.querySelector('canvas');
                if (canvas) {
                    resolve();
                } else {
                    requestAnimationFrame(check);
                }
            };
            check();
        });
    }
}
